import React, { useContext, useState } from 'react';
import PropTypes from 'prop-types';
import {
  BasicCollapsible,
  Box,
  Button,
  Column,
  FlexBox,
  FormField,
  FormInputGroup,
  FormLabel,
  GridContainer,
  Icon,
  ModalDialog,
  ModalDialogBody,
  ModalDialogCloseButton,
  ModalDialogContent,
  ModalDialogFooter,
  ModalDialogHeader,
  ModalDialogNav,
  ModalDialogTitle,
  Row,
  StandardForm,
  TextInput,
  ToggleSwitch,
  ToggleSwitchOffLabel,
  ToggleSwitchOnLabel,
  Typography,
} from '@vp/swan';
import { LocalizationContext } from '@vp/om-translations';
import { DebugModeContext } from '../contexts/DebugModeContext';
import useSiteData from '../../hooks/useSiteData';
import { DebugModPanel } from './DebugModPanel/DebugModPanel';
import { SourcePageContext } from '../contexts/SourcePageContext';
import { LineItemActionOverride } from '../LineItemActionOverride';
import { ORDER_DETAILS_SOURCE_PAGE } from '../../constants/orderdetailsui';
import { DebugExperimentsPanel } from './DebugExperimentsPanel/DebugExperimentsPanel';
import { DebugFeaturePanel } from './DebugFeaturePanel/DebugFeaturePanel';

const DebugTools = ({ shopperId, order }) => {
  const { vistacartTenant: tenant } = useSiteData();
  const { debugEnabled, setDebugEnabled } = useContext(DebugModeContext);
  const { targetLocale, setLocale } = useContext(LocalizationContext);
  const { sourcePage } = useContext(SourcePageContext);
  const { debugPOSTEnabled, setDebugPOSTEnabled } = useContext(
    LineItemActionOverride
  );

  const [isOpen, setIsOpen] = useState(false);

  if (debugEnabled) {
    return (
      <div className="debug-tools">
        <FlexBox
          backgroundColor="light-blue"
          p={7}
          mt={5}
          mb={7}
          flexDirection="row"
          justifyContent="space-between"
        >
          <FlexBox
            flexDirection="column"
            justifyContent="space-between"
            alignItems="stretch"
          >
            {/* Shopper ID */}
            <Typography>
              <b>Shopper ID: </b>
              {shopperId}
            </Typography>
            {/* Tenant */}
            <Typography>
              <b>Tenant: </b>
              {tenant}
            </Typography>
          </FlexBox>
          <FlexBox
            flexDirection="column"
            justifyContent="space-between"
            style={{ minWidth: '10%', width: '240px' }}
            flexWrap="nowrap"
          >
            <Button
              onClick={() => setIsOpen(true)}
              size="mini"
              iconPosition="right"
            >
              Debug Tools
              <Icon alt="" iconType="chevronLeft" />
            </Button>
          </FlexBox>
        </FlexBox>
        <ModalDialog
          variant="panel-right"
          isOpen={isOpen}
          bodyWidth="standard"
          onRequestDismiss={() => {
            setIsOpen(false);
          }}
          onlyRenderWhenOpen
        >
          <ModalDialogContent style={{ maxWidth: '60%', minWidth: '30%' }}>
            <ModalDialogNav>
              <ModalDialogCloseButton accessibleText="Close" />
            </ModalDialogNav>
            <ModalDialogHeader>
              <ModalDialogTitle>Debug Tools</ModalDialogTitle>
            </ModalDialogHeader>
            <ModalDialogBody>
              <FlexBox flexDirection="column" justifyContent="start" pb={5}>
                {/* Shopper ID */}
                <Typography>
                  <b>Shopper ID: </b>
                  {shopperId}
                </Typography>
                {/* Tenant */}
                <Typography>
                  <b>Tenant: </b>
                  {tenant}
                </Typography>
              </FlexBox>

              {/* Order */}
              {order ? (
                <BasicCollapsible
                  heading="Order JSON"
                  mb={5}
                  collapsibleId="Order JSON"
                >
                  <pre
                    style={{
                      fontSize: '10px',
                      overflow: 'auto',
                      maxHeight: '400px',
                    }}
                  >
                    {JSON.stringify(order, null, 2)}
                  </pre>
                </BasicCollapsible>
              ) : null}

              {/* Site data & localization */}
              <BasicCollapsible heading="Site data & localization" mb={5}>
                {/* Tenant */}
                <FlexBox alignItems="center" my={3}>
                  <Typography fontWeight="bold" fontSize="small" mr={3}>
                    Tenant:
                  </Typography>
                  <Typography ml={5}>{tenant}</Typography>
                </FlexBox>

                {/* Locale */}
                <FlexBox alignItems="center" my={3}>
                  <StandardForm variant="horizontal">
                    <GridContainer>
                      <Row component={FormField}>
                        <Column>
                          <FormLabel>Locale:</FormLabel>
                        </Column>
                        <Column>
                          <FormInputGroup>
                            <TextInput
                              ml={5}
                              value={targetLocale}
                              onChange={({ target: { value } }) =>
                                setLocale(value)
                              }
                              size="mini"
                            />
                          </FormInputGroup>
                        </Column>
                      </Row>
                    </GridContainer>
                  </StandardForm>
                </FlexBox>
              </BasicCollapsible>

              {/* Experiments */}
              <BasicCollapsible
                heading="Experiment settings"
                mb={5}
                collapsibleId="Experiment settings"
              >
                <DebugExperimentsPanel />
              </BasicCollapsible>

              {/* Feature toggles */}
              <BasicCollapsible
                heading="Feature settings"
                mb={5}
                collapsibleId="Feature settings"
              >
                <DebugFeaturePanel />
              </BasicCollapsible>

              {/* Order Details overrides */}
              {sourcePage === ORDER_DETAILS_SOURCE_PAGE && (
                <BasicCollapsible
                  heading="Item Action Overrides"
                  mb={5}
                  collapsibleId="Item Action Overrides"
                >
                  {/* Render POST component even when item is not in initial state (assuming product is supported by POST)  */}
                  <FlexBox>
                    <Box>
                      <ToggleSwitch
                        activated={debugPOSTEnabled}
                        onRequestActivatedChange={value =>
                          setDebugPOSTEnabled(value)
                        }
                        hideText
                        aria-labelledby="post-toggle-label"
                      >
                        <ToggleSwitchOnLabel>Enabled</ToggleSwitchOnLabel>
                        <ToggleSwitchOffLabel>Disabled</ToggleSwitchOffLabel>
                      </ToggleSwitch>
                    </Box>
                    <Box>
                      <Typography id="post-toggle-label">
                        Enable POST for all supported items (valid product &
                        design with editable fields
                      </Typography>
                    </Box>
                  </FlexBox>
                </BasicCollapsible>
              )}
              <DebugModPanel />
            </ModalDialogBody>
            <ModalDialogFooter pinned>
              {/* Buttons */}
              <Button
                skin="primary"
                width="full-width"
                size="mini"
                mb={3}
                onClick={() => {
                  setIsOpen(false);
                }}
              >
                Close
              </Button>
              <Button
                skin="secondary"
                width="full-width"
                size="mini"
                onClick={() => {
                  setDebugEnabled(!debugEnabled);
                  // corejs3 has a polyfill for URLSearchParams so it is safe to use
                  const qs = new URLSearchParams(window.location.search).delete(
                    'debug'
                  );
                  window.location.replace(
                    window.location.pathname + (qs !== '' ? '' : qs)
                  );
                }}
              >
                Exit debug mode
              </Button>
            </ModalDialogFooter>
          </ModalDialogContent>
        </ModalDialog>
      </div>
    );
  }

  return null;
};

DebugTools.propTypes = {
  shopperId: PropTypes.string,
  order: PropTypes.object,
};

export default DebugTools;
